import React, { useEffect } from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { CookiesProvider } from 'react-cookie'
import { BrowserRouter as Router } from 'react-router-dom'
import { RecoilRoot } from 'recoil'

import AppMediumProvider from '@/contexts/AppMediumContext'
import SnackbarProvider from '@/contexts/SnackbarContext'

import { ThemeProvider } from '@/theme'

import { ApiProvider } from '@/services/api/ApiContext'

import ErrorHandler from '@/components/ErrorHandler'

import Toast from '@/ui/Toast'

import { AuthProvider } from '@/blocks/Auth/contexts/AuthContext'
import { AccountSettingsProvider } from '@/blocks/Booking/contexts/AccountSettingsContext'

import Routes from './Routes'
import { initDeviceUtils } from './utils/helpers/initDeviceUtils'
import useVisualViewportKeyboardFix from './utils/hooks/useVisualViewportKeyboardFix'
import { IntlContext } from './utils/localization/I18n'
import { LanguageProvider } from './utils/localization/LanguageContext'

import { queryClient } from '@/services'

const PRELOADER_TIME = process.env.NODE_ENV !== 'development' ? 0 : 0

console.log(`%cBuild version: ${VERSION}`, 'color: #0A84FF')

function App() {
  useVisualViewportKeyboardFix()

  useEffect(() => {
    initDeviceUtils()

    setTimeout(() => {
      window.removePreloader?.()
    }, PRELOADER_TIME)
  }, [])

  return (
    <LanguageProvider>
      <ThemeProvider>
        <ErrorHandler>
          <IntlContext>
            <CookiesProvider>
              <RecoilRoot>
                <QueryClientProvider client={queryClient}>
                  <Router
                    basename={process.env.PUBLIC_URL}
                    future={{
                      v7_relativeSplatPath: true,
                      v7_startTransition: true,
                    }}
                  >
                    <ApiProvider>
                      <AppMediumProvider>
                        <AccountSettingsProvider>
                          <SnackbarProvider>
                            <AuthProvider>
                              <Routes />
                              <Toast />
                            </AuthProvider>
                          </SnackbarProvider>
                        </AccountSettingsProvider>
                      </AppMediumProvider>
                    </ApiProvider>
                  </Router>
                  <ReactQueryDevtools />
                </QueryClientProvider>
              </RecoilRoot>
            </CookiesProvider>
          </IntlContext>
        </ErrorHandler>
      </ThemeProvider>
    </LanguageProvider>
  )
}

export default App
